import React, { createContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BrandLogo from './BrandLogo';
import './AdminLayout.css';

/** Lets a page's ← button open the admin menu drawer that AdminLayout owns. */
export const AdminNavContext = createContext<{ openNav: () => void }>({ openNav: () => {} });

interface AdminLayoutProps {
  children: React.ReactNode;
  topbarLabel?: string;
  topbarTitle?: string;
  topbarSubtitle?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  topbarLabel = 'Internal Access',
  topbarTitle = 'Operations Workspace',
  topbarSubtitle,
}) => {
  const location = useLocation();
  const { user } = useAuth();
  // Below 1024px the menu is a left-hand overlay drawer (see AdminLayout.css).
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [navOpen]);

  const isActive = (path: string) => location.pathname.startsWith(path);
  const userLabel = user?.name || user?.email || 'Super Admin';
  const initials = userLabel
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0]?.toUpperCase())
    .join('') || 'SA';

  return (
    <AdminNavContext.Provider value={{ openNav: () => setNavOpen(true) }}>
    <div className="admin-layout">
      <div className={`admin-nav-backdrop${navOpen ? ' is-open' : ''}`} onClick={() => setNavOpen(false)} aria-hidden="true" />
      <aside className={`admin-sidebar${navOpen ? ' admin-sidebar--open' : ''}`}>
        <div className="admin-sidebar-header">
          <BrandLogo height={30} className="admin-sidebar-logo" />
          <span className="admin-sidebar-subtitle">Admin Console</span>
          <button type="button" className="admin-nav-close" onClick={() => setNavOpen(false)} aria-label="Close menu">&#10005;</button>
        </div>
        <nav className="admin-nav">
          <div className="admin-nav-section">
            <span className="admin-nav-section-label">Pricing</span>
            <Link
              to="/admin/pricing/payg"
              className={`admin-nav-link ${isActive('/admin/pricing/payg') ? 'active' : ''}`}
            >
              Pay-as-you-go
            </Link>
            <Link
              to="/admin/pricing/plans"
              className={`admin-nav-link ${isActive('/admin/pricing/plans') ? 'active' : ''}`}
            >
              Subscription Plans
            </Link>
          </div>
          <div className="admin-nav-section">
            <Link
              to="/admin/defaults"
              className={`admin-nav-link ${isActive('/admin/defaults') ? 'active' : ''}`}
            >
              Defaults
            </Link>
          </div>
          <div className="admin-nav-section">
            <span className="admin-nav-section-label">Users</span>
            <Link
              to="/admin/users"
              className={`admin-nav-link ${isActive('/admin/users') ? 'active' : ''}`}
            >
              Registered Users
            </Link>
            <Link
              to="/admin/waitlist"
              className={`admin-nav-link ${isActive('/admin/waitlist') ? 'active' : ''}`}
            >
              Waitlist Users
            </Link>
            <Link
              to="/admin/mailer"
              className={`admin-nav-link ${isActive('/admin/mailer') ? 'active' : ''}`}
            >
              Mailer
            </Link>
          </div>
          <div className="admin-nav-section">
            <Link
              to="/admin/assets"
              className={`admin-nav-link ${isActive('/admin/assets') ? 'active' : ''}`}
            >
              Assets
            </Link>
          </div>
          <div className="admin-nav-section">
            <Link
              to="/admin/spot-workers"
              className={`admin-nav-link ${isActive('/admin/spot-workers') ? 'active' : ''}`}
            >
              Spot Workers
            </Link>
          </div>
        </nav>
        <div className="admin-sidebar-footer">
          <Link to="/dashboard" className="admin-back-link">← Back to App</Link>
        </div>
      </aside>
      <div className="admin-main-shell">
        <header className="admin-topbar">
          <button
            type="button"
            className="admin-nav-toggle"
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
            aria-expanded={navOpen}
          >
            <span className="admin-nav-toggle-bar" />
          </button>
          <div className="admin-topbar-text">
            <span className="admin-topbar-label">{topbarLabel}</span>
            <h2 className="admin-topbar-title">{topbarTitle}</h2>
            {topbarSubtitle && <p className="admin-topbar-subtitle">{topbarSubtitle}</p>}
          </div>
          <div className="admin-user-chip">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt={userLabel} className="admin-user-avatar" />
            ) : (
              <span className="admin-user-avatar admin-user-avatar-fallback">{initials}</span>
            )}
            <div className="admin-user-meta">
              <span className="admin-user-name">{userLabel}</span>
              {user?.email && <span className="admin-user-email">{user.email}</span>}
            </div>
          </div>
        </header>
        <main className="admin-main">{children}</main>
      </div>
    </div>
    </AdminNavContext.Provider>
  );
};

export default AdminLayout;
